import { createAxiosInstance } from './axios.js'
import { translationService } from '../services/translation'

const api = createAxiosInstance()
api.interceptors.request.use(config => {
  const language = translationService.getLanguage()
  config.headers['Accept-Language'] = language
  config.headers['X-Language'] = language
  return config
})

let cachedBooks = null
let cacheTimestamp = null
let cachedCategories = null
let categoriesCacheTimestamp = null
const CACHE_DURATION = 5 * 60 * 1000

// Fetch and cache categories
async function fetchCategories () {
  const now = Date.now()
  if (
    cachedCategories &&
    categoriesCacheTimestamp &&
    now - categoriesCacheTimestamp < CACHE_DURATION
  ) {
    return cachedCategories
  }

  try {
    const response = await api.get('/api/categories', {
      params: { limit: 1000 }
    })
    cachedCategories = response.data.categories || []
    categoriesCacheTimestamp = now
    return cachedCategories
  } catch (error) {
    console.error('Error fetching categories:', error)
    return []
  }
}

// Map genre slugs to category names
function mapGenreSlugsToNames (genreSlugs, categories) {
  if (!Array.isArray(genreSlugs)) return []
  if (!Array.isArray(categories) || categories.length === 0) return genreSlugs

  const categoryMap = {}
  categories.forEach(cat => {
    if (cat.slug) {
      categoryMap[cat.slug] = cat.name
    }
  })

  return genreSlugs
    .map(slug => {
      if (typeof slug === 'string') {
        return categoryMap[slug] || slug
      }
      return slug
    })
    .filter(name => name)
}

function transformBook (
  book,
  formats = null,
  retailers = null,
  categories = null,
  digitalProducts = null,
  testAccess = null
) {
  const genreArray = Array.isArray(book.genre)
    ? book.genre
    : book.genre?.value || []
  const price =
    typeof book.price === 'number' ? book.price : parseFloat(book.price || 0)

  const formatPrice = p => `$${parseFloat(p).toFixed(2)}`

  let transformedFormats = []
  if (formats && Array.isArray(formats) && formats.length > 0) {
    transformedFormats = formats.map(f => {
      const formatPriceVal =
        typeof f.price === 'number' ? f.price : parseFloat(f.price || 0)
      const discountPrice = f.discount_percentage
        ? (formatPriceVal * (1 - f.discount_percentage / 100)).toFixed(2)
        : null
      return {
        id: f.id,
        type: f.format_type.charAt(0).toUpperCase() + f.format_type.slice(1),
        formatType: f.format_type,
        price: formatPrice(discountPrice || formatPriceVal),
        originalPrice: formatPrice(formatPriceVal),
        discountPercentage: f.discount_percentage || null,
        isbn: f.isbn,
        ebookUrl: f.ebook_url || null,
        audiobookUrl: f.audiobook_url || null,
        isUnavailable: f.is_unavailable || false,
        // Digital-product info for in-app reading / free samples. Only safe
        // scalars are exposed by the API — never internal file ids.
        digitalProductId: f.digital_product_id || null,
        sampleAvailable: !!f.sample_available,
        digitalPrice: f.digital_price != null ? f.digital_price : null,
        retailers:
          f.retailers && Array.isArray(f.retailers)
            ? f.retailers.filter(r => r && r.name && r.link)
            : []
      }
    })
  } else {
    const discountPrice = book.discount_percentage
      ? (price * (1 - book.discount_percentage / 100)).toFixed(2)
      : null
    if (book.format === 'hardcover' || book.format === 'paperback') {
      transformedFormats.push({
        id: null,
        type: book.format.charAt(0).toUpperCase() + book.format.slice(1),
        formatType: book.format,
        price: formatPrice(discountPrice || price),
        originalPrice: formatPrice(price),
        discountPercentage: book.discount_percentage || null,
        isbn: book.isbn || '',
        ebookUrl: book.ebook_url || null,
        audiobookUrl: book.audiobook_url || null
      })
    }
  }

  const publishedDate = book.published_date
    ? new Date(book.published_date).toLocaleDateString('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : ''

  // Map genre slugs to category names
  const genreNames = categories
    ? mapGenreSlugsToNames(genreArray, categories)
    : genreArray

  return {
    id: book.id,
    title: book.title,
    author: book.author,
    price:
      transformedFormats.length > 0
        ? transformedFormats[0].price
        : formatPrice(price),
    description: book.description,
    coverImage: book.cover_image || '',
    imageCropDimensions: book.image_crop_dimensions || null,
    publishedDate,
    pages: book.page_count || null,
    isbn: book.isbn || '',
    publisher: 'Barry & Bonna Publishing',
    bestseller: false,
    formats:
      transformedFormats.length > 0
        ? transformedFormats
        : [
            {
              id: null,
              type: 'Hardcover',
              formatType: 'hardcover',
              price: formatPrice(price),
              originalPrice: formatPrice(price),
              discountPercentage: null,
              isbn: book.isbn || '',
              ebookUrl: null,
              audiobookUrl: null
            }
          ],
    retailers:
      retailers && Array.isArray(retailers)
        ? retailers.filter(r => r && r.id && r.name && r.link)
        : [],
    genres: genreNames,
    praise: [],
    authorBio: '',
    authorImage: null,
    subtitle: '',
    isUnavailable: book.is_unavailable || false,
    // Admin-only "test" book: kept out of the catalog, but still reachable by an
    // admin previewing it and by a customer who bought it before it was flagged.
    isTest: book.is_test || false,
    // Which of those two the viewer is ('admin' | 'owner'), so the page can say
    // the right thing. Null for every book that is not flagged as a test.
    testAccess: testAccess || null,
    // Soft-deleted by the publisher: the page stays viewable (purchase links
    // keep working) but the book is no longer for sale.
    isDeleted: !!book.deleted_at,
    deletedAt: book.deleted_at || null,
    alsoAvailableFrom: book.also_available_from || null,
    // Digital products (e-book / audiobook) with their sample availability.
    // Fallback for showing a sample even when there is no matching format row.
    digitalProducts:
      digitalProducts && Array.isArray(digitalProducts)
        ? digitalProducts.map(d => ({
            id: d.id,
            format: d.format,
            fileFormat: d.file_format,
            sampleAvailable: !!d.sample_available,
            digitalPrice: d.digital_price != null ? d.digital_price : null
          }))
        : []
  }
}

async function fetchBooks (category = null, search = null) {
  const now = Date.now()

  if (
    cachedBooks &&
    cacheTimestamp &&
    now - cacheTimestamp < CACHE_DURATION &&
    !category &&
    !search
  ) {
    return cachedBooks
  }

  try {
    // Fetch categories for genre name mapping
    const categories = await fetchCategories()

    const params = new URLSearchParams()
    if (category) {
      params.append('category', category)
    }
    if (search) {
      params.append('search', search)
    }

    const url = `/api/books${params.toString() ? '?' + params.toString() : ''}`
    const response = await api.get(url)
    const books = (
      response.data.pagination ? response.data.books : response.data || []
    ).map(book => transformBook(book, null, null, categories))

    if (!category && !search) {
      cachedBooks = books
      cacheTimestamp = now
    }

    return books
  } catch (error) {
    console.error('Failed to fetch books:', error)
    return []
  }
}

export async function getAllBooks () {
  return await fetchBooks()
}

export async function getBookById (id) {
  const result = await getBookByIdDetailed(id)
  return result.book
}

// Like getBookById, but reports why no book came back so callers can tell a
// genuinely missing/hidden book ("not-found", e.g. deleted or test-only) from a
// server/network failure ("error").
export async function getBookByIdDetailed (id) {
  try {
    // Fetch categories for genre name mapping
    const categories = await fetchCategories()

    const bookId = typeof id === 'string' ? parseInt(id, 10) : id
    const response = await api.get(`/api/books/${bookId}`)
    if (response.data) {
      if (response.data.book) {
        return {
          status: 'ok',
          book: transformBook(
            response.data.book,
            response.data.formats,
            response.data.retailers,
            categories,
            response.data.digital_products,
            response.data.test_access
          )
        }
      }
      return { status: 'ok', book: transformBook(response.data, null, null, categories) }
    }
    return { status: 'not-found', book: null }
  } catch (error) {
    const status = error && error.response ? error.response.status : null
    if (status === 400 || status === 404) {
      return { status: 'not-found', book: null }
    }
    console.error(`Failed to fetch book ${id}:`, error)
    return { status: 'error', book: null }
  }
}

export async function getFeaturedBooks () {
  try {
    const categories = await fetchCategories()
    const response = await api.get('/api/slider-books')
    return (response.data || []).map(book =>
      transformBook(book, null, null, categories)
    )
  } catch (error) {
    console.error('Failed to fetch slider books:', error)
    return []
  }
}

export async function getBestsellers () {
  try {
    const categories = await fetchCategories()
    const response = await api.get('/api/section-books/bestsellers')
    return (response.data || []).map(book =>
      transformBook(book, null, null, categories)
    )
  } catch (error) {
    console.error('Failed to fetch bestsellers:', error)
    return []
  }
}

export async function getNewReleases () {
  const books = await fetchBooks()
  return books.slice(-4).reverse()
}

export async function getTrendingBooks () {
  const books = await fetchBooks()
  return books.slice(0, 4)
}

export async function getRecommendedReads () {
  try {
    const categories = await fetchCategories()
    const response = await api.get('/api/section-books/recommended-reads')
    return (response.data || []).map(book =>
      transformBook(book, null, null, categories)
    )
  } catch (error) {
    console.error('Failed to fetch recommended reads:', error)
    return []
  }
}

export async function getComingSoon () {
  try {
    const categories = await fetchCategories()
    const response = await api.get('/api/section-books/coming-soon')
    return (response.data || []).map(book =>
      transformBook(book, null, null, categories)
    )
  } catch (error) {
    console.error('Failed to fetch coming soon:', error)
    return []
  }
}

export async function searchBooks (query) {
  return await fetchBooks(null, query)
}

export async function getBooksByCategory (category) {
  return await fetchBooks(category)
}

export async function getFeaturedBooksForPage () {
  try {
    const categories = await fetchCategories()
    const response = await api.get('/api/slider-books')
    return (response.data || []).map(book =>
      transformBook(book, null, null, categories)
    )
  } catch (error) {
    console.error('Failed to fetch slider books:', error)
    return []
  }
}
